import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { panelVariants, transition } from '../lib/motion.js';
import { useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { getGuestAuthIssueCopy, mapAuthError } from '../lib/guestCopy.js';
import DjSocialLinks, { getDjDisplayName } from './DjSocialLinks.jsx';

/**
 * Écran d’accueil obligatoire à l’arrivée sur une soirée live : entrée invité,
 * e-mail optionnel (repliable).
 */
export default function EventGate({
  event,
  barAuthReady,
  barUser,
  barAuthReason,
  barAuthSignInError,
  barAuthProfileError,
  onEnterSoiree,
  onOpenAccountAdvanced,
  onRetryAuth,
  paidQueueUiEnabled = true,
}) {
  const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState(null);
  const [emailOk, setEmailOk] = useState(null);
  const [showEmail, setShowEmail] = useState(false);

  const barName = event?.establishments?.name ?? 'Bar';
  const djName = getDjDisplayName(event?.djs);

  async function submitEmailAuth(e) {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setEmailErr('Indique une adresse e-mail valide.');
      return;
    }
    if (!password || password.length < 8) {
      setEmailErr('Le mot de passe doit faire au moins 8 caractères.');
      return;
    }
    setEmailErr(null);
    setEmailOk(null);
    setEmailBusy(true);

    const action =
      authMode === 'signup'
        ? supabase.auth.signUp({ email: clean, password })
        : supabase.auth.signInWithPassword({ email: clean, password });

    const { error } = await action;
    setEmailBusy(false);

    if (error) {
      setEmailErr(mapAuthError(error));
      return;
    }
    setEmailOk(authMode === 'signup' ? 'Compte créé. Entrée…' : 'Connecté. Entrée…');
    // Auto-entrée : l'auth e-mail fait entrer directement, plus de second tap.
    onEnterSoiree?.();
  }

  const reduceMotion = useReducedMotion();
  const authBlocked = barAuthReady && !barUser;
  const authIssue = authBlocked && barAuthReason ? getGuestAuthIssueCopy(barAuthReason) : null;
  const devDetail = import.meta.env.DEV
    ? barAuthSignInError || barAuthProfileError || null
    : null;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-bg">
      <motion.div
        className="container-mobile relative flex min-h-0 flex-1 flex-col justify-center overflow-y-auto py-8"
        variants={panelVariants}
        initial={reduceMotion ? false : 'initial'}
        animate="animate"
        transition={transition(reduceMotion, 'panel')}
      >
        <Link
          to="/"
          className="absolute right-4 top-4 rounded-lg px-3 py-1.5 text-xs text-white/45 hover:text-white/75"
          aria-label="Accueil — changer de soirée"
        >
          Changer de soirée
        </Link>
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
          Bienvenue
        </p>
        <h1 className="font-display mt-2 text-center text-2xl leading-tight tracking-tight text-white sm:text-3xl">
          {event?.name ?? 'Soirée'}
        </h1>
        <p className="mt-2 text-center text-sm text-white/65">
          {barName}
          {djName ? (
            <>
              <span className="text-white/35"> · </span>DJ {djName}
            </>
          ) : null}
        </p>
        <DjSocialLinks dj={event?.djs} compact className="mt-4 justify-center" />

        <p className="mx-auto mt-8 max-w-[340px] text-center text-sm leading-relaxed text-white/55">
          Entre en invité sans mot de passe. Tu pourras ajouter un e-mail plus tard depuis ton compte.
        </p>

        {authIssue ? (
          <div className="mx-auto mt-6 max-w-[400px] rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-center">
            <p className="text-sm font-semibold text-amber-50">{authIssue.title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-amber-100/90">{authIssue.body}</p>
            {onRetryAuth ? (
              <button
                type="button"
                className="btn-secondary mt-3 w-full py-2.5 text-xs"
                onClick={() => void onRetryAuth()}
              >
                Réessayer la connexion
              </button>
            ) : null}
            {devDetail ? (
              <p className="mt-2 rounded-lg bg-black/30 px-2 py-1.5 text-left font-mono text-[10px] text-amber-50/80">
                Dev : {devDetail}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="mx-auto mt-8 w-full max-w-[400px] space-y-3">
          <button
            type="button"
            className="btn-primary min-h-[48px] w-full py-4 text-base font-semibold"
            onClick={() => onEnterSoiree?.()}
          >
            Entrer sur la soirée
          </button>

          <p className="text-center text-[11px] text-white/45">
            {paidQueueUiEnabled
              ? 'Mode invité sur cet appareil : votes, suggestions et cagnotte.'
              : 'Mode invité sur cet appareil : votes et suggestions.'}
          </p>

          {emailOk ? (
            <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-center text-xs text-emerald-200">
              {emailOk}
            </p>
          ) : null}

          <button
            type="button"
            className="btn-ghost w-full py-2.5 text-sm text-white/65"
            onClick={() => setShowEmail((v) => !v)}
            aria-expanded={showEmail}
          >
            {showEmail ? 'Masquer la connexion e-mail' : 'Se connecter par e-mail (optionnel)'}
          </button>

          {showEmail ? (
            <form
              onSubmit={submitEmailAuth}
              className="rounded-2xl border border-border bg-bg-card p-4"
            >
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${
                    authMode === 'signin'
                      ? 'border-accent/45 bg-bg-soft text-text'
                      : 'border-border text-white/60'
                  }`}
                  onClick={() => setAuthMode('signin')}
                >
                  J&apos;ai déjà un compte
                </button>
                <button
                  type="button"
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${
                    authMode === 'signup'
                      ? 'border-accent/45 bg-bg-soft text-text'
                      : 'border-border text-white/60'
                  }`}
                  onClick={() => setAuthMode('signup')}
                >
                  Créer un compte
                </button>
              </div>
              <p className="mt-3 text-xs text-white/50">
                {authMode === 'signup'
                  ? 'Crée ton compte client pour retrouver ta progression d\'un téléphone à l\'autre.'
                  : 'Connecte-toi pour retrouver ta progression sur cet appareil.'}
              </p>
              <input
                className="input mt-3"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="toi@mail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                className="input mt-2"
                type="password"
                autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                placeholder="Mot de passe (8 caractères min.)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
              {emailErr ? (
                <p className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {emailErr}
                </p>
              ) : null}
              <button type="submit" disabled={emailBusy} className="btn-secondary mt-3 w-full py-3 text-sm">
                {emailBusy
                  ? authMode === 'signup'
                    ? 'Création…'
                    : 'Connexion…'
                  : authMode === 'signup'
                    ? 'Créer mon compte'
                    : 'Se connecter'}
              </button>
            </form>
          ) : null}

          {onOpenAccountAdvanced ? (
            <button
              type="button"
              className="btn-ghost w-full py-2 text-xs text-white/45 hover:text-white/65"
              onClick={() => onOpenAccountAdvanced()}
            >
              Pseudo et options compte
            </button>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
}
